import { Injectable } from "@angular/core";
import { ItemDataService } from "../item-data.service";
import { TieredUnique, UniqueTierType, UniqueItem, UniqueCategory, CustomTieredUnique, PricedItemWithImage, UniquesTierValueList, UniquesTierValue } from "../../models/item-data-models";
import { trackError, logError } from "../../app.config";
import { Filter } from "../../models/filter-models";
import { poe2 } from "../../../game";
import { EconomyService } from "../economy.service";
import { EndgameContent, TieringMode } from "../../models/enums";
import { formatTierThresholdValue, getEffectiveTierThresholdValue } from "../../tier-list-tab/tier-value-helpers";

export const ORDERED_UNIQUE_TIERS: UniqueTierType[] = ['Other', 'Potential', 'Good', 'Excellent'];

@Injectable({
  providedIn: 'root'
})
export class UniquesTierService {
  private classSpecificUniquesData: Record<string, string[]>;
  private uniqueBossDrops: { unique: string, bossName: string, content?: EndgameContent }[];
  private uniquesList!: UniqueItem[];

  private defaultTieredUniques!: TieredUnique[];

  private tieredUniques: Record<UniqueTierType, TieredUnique[]> = {
    Excellent: [] as TieredUnique[],
    Good: [] as TieredUnique[],
    Potential: [] as TieredUnique[],
    Restricted: [] as TieredUnique[],
    Class: [] as TieredUnique[],
    Other: [] as TieredUnique[],
  };

  private tierCutoffValues: {
    mode: TieringMode,
    values: UniquesTierValueList
  }[] = poe2 ? [
    // ********* POE 2 *********
    {
      mode: 'Early',
      values: [
        { tier: 'Excellent', value: 25, baseCurrency: 'Exalted Orb' },
        { tier: 'Good', value: 2.5, baseCurrency: 'Exalted Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Exalted Orb' },
      ]
    },
    {
      mode: 'Normal',
      values: [
        { tier: 'Excellent', value: 50, baseCurrency: 'Exalted Orb' },
        { tier: 'Good', value: 5, baseCurrency: 'Exalted Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Exalted Orb' },
      ]
    },
    {
      mode: 'Strict',
      values: [
        { tier: 'Excellent', value: 200, baseCurrency: 'Exalted Orb' },
        { tier: 'Good', value: 10, baseCurrency: 'Exalted Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Exalted Orb' },
      ]
    },
    {
      mode: 'VeryStrict',
      values: [
        { tier: 'Excellent', value: 5, baseCurrency: 'Divine Orb' },
        { tier: 'Good', value: 0.5, baseCurrency: 'Divine Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Exalted Orb' },
      ]
    }
  ] : [
    // ********* POE 1 *********
    {
      mode: 'Early',
      values: [
        { tier: 'Excellent', value: 25, baseCurrency: 'Chaos Orb' },
        { tier: 'Good', value: 2.5, baseCurrency: 'Chaos Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Chaos Orb' },
      ]
    },
    {
      mode: 'Normal',
      values: [
        { tier: 'Excellent', value: 50, baseCurrency: 'Chaos Orb' },
        { tier: 'Good', value: 5, baseCurrency: 'Chaos Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Chaos Orb' },
      ]
    },
    {
      mode: 'Strict',
      values: [
        { tier: 'Excellent', value: 200, baseCurrency: 'Chaos Orb' },
        { tier: 'Good', value: 10, baseCurrency: 'Chaos Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Chaos Orb' },
      ]
    },
    {
      mode: 'VeryStrict',
      values: [
        { tier: 'Excellent', value: 5, baseCurrency: 'Divine Orb' },
        { tier: 'Good', value: 0.5, baseCurrency: 'Divine Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Exalted Orb' },
      ]
    }
  ];

  private uniquesTierValueList: UniquesTierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;

  constructor(private itemDataService: ItemDataService, private economyService: EconomyService) {
    this.classSpecificUniquesData = this.itemDataService.getClassSpecificUniques();
    this.uniqueBossDrops = this.itemDataService.getUniqueBossDrops();
    this.uniquesList = this.itemDataService.getUniquesList();
  }

  setTieringMode(mode: TieringMode, filter: Filter) {
    const valueList = mode === 'Custom' ? filter.uniquesTieringModeCustom : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList) {
      // Should never happen, but apparently sometimes does. Use defaul 'Normal'.
      logError('UniquesTierService.setTieringMode: No value list found for mode ' + mode + '. Using default Normal.');
      this.uniquesTierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;
    } else {
      this.uniquesTierValueList = valueList;
    }
    filter.uniquesTieringMode = mode;
    this.defaultTieredUniques = this.buildDefaultTieredUniques();
    this.combineTieredUniques(filter, this.defaultTieredUniques, false);
  }

  buildDefaultTieredUniques(previous = false) {
    return this.buildDefaultTieredUniquesFor(this.uniquesTierValueList, previous);
  }

  // Pure simulation: returns a local tier list for the given cutoffs without mutating service state.
  private buildDefaultTieredUniquesFor(valueList: UniquesTierValueList, previous = false): TieredUnique[] {
    const currentValues = structuredClone(previous ? this.economyService.getPreviousUniquesData() : this.economyService.getUniquesData());
    const previousValues = previous ? [] : structuredClone(this.buildDefaultTieredUniquesFor(valueList, true));

    const goodTierThreshold = valueList.find(t => t.tier === 'Good');
    const highValue = goodTierThreshold ? this.toBaseCurrencyValue(goodTierThreshold) : 10;
    const downgradeExcellentByLowestValueThreshold = highValue;
    const downgradeExcellentByAverageValueThreshold = highValue * 2;
    const downgradeGoodThreshold = highValue / 2;

    const uniquesWithExtraInfo = currentValues.map(uv => {
      const unique = this.uniquesList.find(u => u.name === uv.name);
      const bossDropEntry = this.uniqueBossDrops.find(b => b.unique === uv.name);
      if (!unique) return null;
      return {
        ...uv,
        baseType: unique!.baseType,
        bossDrop: bossDropEntry?.bossName,
        bossDropContent: bossDropEntry?.content,
        image: unique!.image ? '/img/uniques/' + unique.image : undefined,
        category: unique!.category || null,
        slots: unique!.slots,
        dustValue: unique!.dustValue,
        dustValuePerSlot: unique!.dustValuePerSlot
      };
    }).filter(u => u !== null);

    const sortedUniqueValues = uniquesWithExtraInfo.sort((a, b) => (b.price || 0) - (a.price || 0));

    // Group by base type
    const groupedByBaseType = sortedUniqueValues.reduce((acc, item) => {
      acc[item.baseType] = acc[item.baseType] || [];
      acc[item.baseType].push({ ...item });
      return acc;
    }, {} as Record<string, (PricedItemWithImage & { category: UniqueCategory })[]>);

    const tieredUniques = Object.entries(groupedByBaseType).map(([baseType, uniques]) => {
      const uniquesWithValues = uniques.filter(u => u.price);
      if (uniquesWithValues.length === 0) return null;

      const nonDroppableBossTypes = this.itemDataService.getNonDroppableUniqueBossTypes();
      const isNonDroppableUnique = (unique: PricedItemWithImage): boolean =>
        !!unique.bossDrop && nonDroppableBossTypes.includes(unique.bossDrop);
      const droppableUniquesWithValues = uniquesWithValues.filter(u => !isNonDroppableUnique(u));

      const uniquesWithoutBossDrops = uniques.filter(u => !u.bossDrop);
      const lowestValue = Math.min(...uniquesWithoutBossDrops.map(u => u.price || 0));
      const avgValue = Math.round((uniquesWithoutBossDrops.reduce((sum, u) => sum + (u.price || 0), 0) / uniquesWithoutBossDrops.length) * 10) / 10;

      const tierPrice = droppableUniquesWithValues[0]?.price || 0;
      let tier: string = valueList.filter(threshold => tierPrice >= this.toBaseCurrencyValue(threshold))[0]?.tier;

      const restrictedDropTypes = this.itemDataService.getRestrictedDropTypesToTreatAsRegular();
      const isDropRestricted = (unique: PricedItemWithImage): boolean =>
        !!unique.bossDrop && !restrictedDropTypes.includes(unique.bossDrop);

      const allUniquesDropRestricted = uniques.every(isDropRestricted);
      const highValueUniques = droppableUniquesWithValues.filter(u => (u.price || 0) >= highValue);
      const hasMultipleUniques = uniquesWithValues.length > 1;
      const hasHighValueUnique = highValueUniques.length > 0;

      if (!allUniquesDropRestricted && hasMultipleUniques && hasHighValueUnique) {
        const hasHighValueTreatableAsRegular = highValueUniques.some(u => !isDropRestricted(u));

        // If all of the uniques which have a value of more than 10 are restricted, then the tier is restricted
        if (!hasHighValueTreatableAsRegular) {
          tier = 'Restricted';
        } else {
          const shouldDowngradeExcellent = tier === 'Excellent' && (lowestValue <= downgradeExcellentByLowestValueThreshold || avgValue <= downgradeExcellentByAverageValueThreshold);
          const shouldDowngradeGood = tier === 'Good' && (lowestValue <= downgradeGoodThreshold || avgValue <= downgradeGoodThreshold);

          if (shouldDowngradeExcellent || shouldDowngradeGood) {
            tier = 'Potential';
          }
        }
      }

      const minimalItemWithTier = this.itemDataService.getMinimalUniqueTierListData().find(m => m.baseType === baseType);
      let manualTier = undefined;
      if (minimalItemWithTier) {
        if (ORDERED_UNIQUE_TIERS.indexOf(tier as UniqueTierType) < ORDERED_UNIQUE_TIERS.indexOf(minimalItemWithTier.tier as UniqueTierType)) {
          tier = minimalItemWithTier.tier;
          manualTier = true;
        }
      }

      if (droppableUniquesWithValues.length === 0) {
        tier = 'Other';
      }

      const tieredUnique = {
        baseType: baseType,
        tier,
        manualTier,
        category: uniques[0].category,
        averageValue: avgValue,
        lowestValue: lowestValue,
        allUniques: uniques.map(u => ({
          name: u.name,
          image: u.image,
          type: u.type,
          price: u.price || 0,
          bossDrop: u.bossDrop,
          bossDropContent: u.bossDropContent,
          dustValue: u.dustValue,
          slots: u.slots,
          ts: u.ts,
        })),
        nonRestrictedUniques: uniquesWithoutBossDrops.map(u => ({ name: u.name,
          image: u.image,
          type: u.type,
          price: u.price || 0,
          bossDrop: u.bossDrop,
          bossDropContent: u.bossDropContent,
          dustValue: u.dustValue,
          slots: u.slots,
          ts: u.ts,
        }))
      } as TieredUnique;

      if (!previous) {
        const previousTieredUnique = previousValues.find(i => i.baseType === baseType);
        if (previousTieredUnique) {
          tieredUnique.previousAverageValue = previousTieredUnique.averageValue;
          tieredUnique.previousTier = previousTieredUnique.tier;
          tieredUnique.movedUp = previousTieredUnique.tier && ORDERED_UNIQUE_TIERS.indexOf(tieredUnique.tier) - ORDERED_UNIQUE_TIERS.indexOf(previousTieredUnique.tier);
          tieredUnique.movedDown = previousTieredUnique.tier && ORDERED_UNIQUE_TIERS.indexOf(previousTieredUnique.tier) - ORDERED_UNIQUE_TIERS.indexOf(tieredUnique.tier);
        }
      }

      return tieredUnique;
    }).filter(item => item !== null);

    return tieredUniques;
  }

  combineTieredUniques(filter: Filter | null, previousDefaultTieredItems?: (TieredUnique | CustomTieredUnique)[], simulate?: boolean): TieredUnique[] {
    const clone = structuredClone(
      previousDefaultTieredItems && previousDefaultTieredItems.length > 0
        ? previousDefaultTieredItems
        : this.defaultTieredUniques
    ) as TieredUnique[];

    if (simulate) return clone;

    filter!.showClassSpecificUniquesForClasses.forEach(className => {
      const uniquesForClass = this.classSpecificUniquesData[className];
      if (uniquesForClass) {
        for (const unique of uniquesForClass) {
          const uniqueItem = this.uniquesList.find(u => u.name === unique);
          if (!uniqueItem) continue;

          const existingItem = clone.find(item => item.baseType === uniqueItem!.baseType);
          if (existingItem && existingItem.tier === 'Other') {
            existingItem.tier = 'Class';
            existingItem.classSpecific = className;
          }
        }
      }
    });

    filter!.customUniqueTiers.forEach(customItem => {
      const existingItem = clone.find(item => item.baseType === customItem.baseType);
      if (existingItem) {
        existingItem.originalTier = existingItem.tier;
        existingItem.tier = customItem.tier;
      } else {
        trackError(new Error('Unexpected custom unique: ' + customItem.baseType));
      }
    });

    // Flag high dust Other/Potential bases (does not change tier); always evaluate from filter params
    clone.forEach(item => {
      item.highDustValue = undefined;
      item.averageNonRestrictedDustValue = undefined;
      item.minNonRestrictedDustValue = undefined;

      if (item.tier !== 'Other' && item.tier !== 'Potential') return;
      if (!item.nonRestrictedUniques.length) return;

      const category = item.category;
      if (category === UniqueCategory.Jewels || category === UniqueCategory.Flasks
        || category === UniqueCategory.Maps || category === UniqueCategory.Other) {
        return;
      }
      if (!filter!.showHighDustValueUniquesIncludeNonJewellery && category !== UniqueCategory.Jewellery) {
        return;
      }

      const averageNonRestrictedDustValue = item.nonRestrictedUniques.reduce((sum, unique) => sum + (unique.dustValue || 0), 0) / item.nonRestrictedUniques.length;
      const minNonRestrictedDustValue = item.nonRestrictedUniques.reduce((min, unique) => Math.min(min, unique.dustValue || 0), Infinity);
      const applySizeDiscount = filter!.showHighDustValueUniquesDiscountBySize !== false;
      const correctedAverage = applySizeDiscount
        ? this.calculateDiscountedDustValue(averageNonRestrictedDustValue, item.allUniques[0].slots)
        : averageNonRestrictedDustValue;
      const correctedMinimum = applySizeDiscount
        ? this.calculateDiscountedDustValue(minNonRestrictedDustValue, item.allUniques[0].slots)
        : minNonRestrictedDustValue;

      const minAverage = filter!.showHighDustValueUniquesMinAverage ?? 15000;
      const minDust = filter!.showHighDustValueUniquesMinDust ?? 10000;
      if (correctedAverage && correctedAverage >= minAverage
        && correctedMinimum && correctedMinimum >= minDust) {
        item.highDustValue = true;
        item.averageNonRestrictedDustValue = Math.round(correctedAverage);
        item.minNonRestrictedDustValue = Math.round(correctedMinimum);
      }
    });

    this.tieredUniques['Excellent'].length = 0;
    this.tieredUniques['Excellent'].push(...clone.filter(i => i.tier === 'Excellent'));
    this.tieredUniques['Good'].length = 0;
    this.tieredUniques['Good'].push(...clone.filter(i => i.tier === 'Good'));
    this.tieredUniques['Potential'].length = 0;
    this.tieredUniques['Potential'].push(...clone.filter(i => i.tier === 'Potential'));
    this.tieredUniques['Restricted'].length = 0;
    this.tieredUniques['Restricted'].push(...clone.filter(i => i.tier === 'Restricted'));
    this.tieredUniques['Class'].length = 0;
    this.tieredUniques['Class'].push(...clone.filter(i => i.tier === 'Class'));
    this.tieredUniques['Other'].length = 0;
    this.tieredUniques['Other'].push(...clone.filter(i => i.tier === 'Other'));

    return clone;
  }

  calculateDiscountedDustValue(averageNonRestrictedDustValue: number, slots?: number) {
    if (!slots) return averageNonRestrictedDustValue;

    // Calculate discounted dust value based on slots using hard-coded cut-off points
    let slotDiscount: number;
    switch (slots) {
      case 1: slotDiscount = 1.0; break;
      case 2: slotDiscount = 0.9; break;
      case 3: slotDiscount = 0.7; break;
      case 4: slotDiscount = 0.6; break;
      case 6: slotDiscount = 0.4; break;
      case 8: slotDiscount = 0.3; break;
      default: slotDiscount = 0.3; break;  // Default to 30%
    }
    return averageNonRestrictedDustValue * slotDiscount;
  }

  getDefaultTieredUniques() {
    return this.defaultTieredUniques;
  }

  getTieredUniques() {
    return this.tieredUniques;
  }

  getSelectedTierCutoffValues() {
    return this.uniquesTierValueList;
  }

  getTierValueListForFilter(filter: Filter): UniquesTierValueList {
    if (filter.uniquesTieringMode === 'Custom') {
      return filter.uniquesTieringModeCustom;
    }
    return this.tierCutoffValues.find(t => t.mode === filter.uniquesTieringMode)?.values
      ?? this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;
  }

  getHighValueThreshold(filter: Filter): number {
    const goodTierThreshold = this.getTierValueListForFilter(filter).find(t => t.tier === 'Good');
    return goodTierThreshold ? this.toBaseCurrencyValue(goodTierThreshold) : 10;
  }

  private toBaseCurrencyValue(threshold: UniquesTierValue): number {
    const currentCurrencyData = this.economyService.getCurrencyData();
    const exaltValue = poe2 ? 1 : (currentCurrencyData.find(item => item.name === 'Exalted Orb')?.price ?? 1);
    const chaosValue = poe2 ? currentCurrencyData.find(item => item.name === 'Chaos Orb')?.price ?? 10 : 1;
    const divineValue = currentCurrencyData.find(item => item.name === 'Divine Orb')?.price ?? 200;

    return getEffectiveTierThresholdValue(threshold) * (
      threshold.baseCurrency === 'Chaos Orb' ? chaosValue
        : threshold.baseCurrency === 'Divine Orb' ? divineValue
          : exaltValue
    );
  }

  getTierCutoffValues() {
    return this.tierCutoffValues;
  }

  getDisplayValueForMode(mode: TieringMode, tier: UniqueTierType, filter?: Filter | null) {
    const valueList = mode === 'Custom'
      ? filter?.uniquesTieringModeCustom
      : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList) return null;
    return this.getDisplayValue(tier, valueList);
  }

  // Top-value base types that would fall into Other under the given mode (ignores custom/class overrides).
  getMostValuableOtherItemsForMode(
    mode: TieringMode,
    count = 3,
    filter?: Filter | null,
  ): { names: string[]; total: number } {
    const valueList = mode === 'Custom'
      ? filter?.uniquesTieringModeCustom
      : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList?.length) return { names: [], total: 0 };

    const other = this.buildDefaultTieredUniquesFor(valueList)
      .filter(item => item.tier === 'Other')
      .map(item => ({
        name: item.baseType,
        value: Math.max(0, ...item.allUniques.map(u => u.price || 0)),
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);

    return {
      names: other.slice(0, count).map(item => item.name),
      total: other.length,
    };
  }

  getDisplayValue(tier: UniqueTierType, valueList: UniquesTierValueList = this.uniquesTierValueList): { priceLabel: string, priceBaseCurrency?: string, priceBaseCurrencyImage?: string } {
    const tierThreshold = valueList.find(t => t.tier === tier);
    let priceLabel = tierThreshold ? '≥ ' + formatTierThresholdValue(tierThreshold) : undefined;
    let priceBaseCurrency = tierThreshold?.baseCurrency;
    let priceBaseCurrencyImage = priceBaseCurrency ? this.itemDataService.getFlatItemDataByBaseTypeName(priceBaseCurrency)?.baseType.image : undefined;

    if (tier === 'Other' || tier === 'Class') {
      const goodTierThreshold = valueList.find(t => t.tier === 'Good');
      priceLabel = goodTierThreshold ? '≤ ' + formatTierThresholdValue(goodTierThreshold) : undefined;
      priceBaseCurrency = goodTierThreshold?.baseCurrency;
      priceBaseCurrencyImage = priceBaseCurrency ? this.itemDataService.getFlatItemDataByBaseTypeName(priceBaseCurrency)?.baseType.image : undefined;
    }

    return {
      priceLabel: priceLabel!,
      priceBaseCurrency,
      priceBaseCurrencyImage
    };
  }
}
